/**
 * #873 Outcome positions sort (web, Android `PositionSort`, iOS `PositionSort`: same ids, same order, same ties).
 *
 * - value_desc (default): the derive order, largest value first.
 * - outcome_asc / outcome_desc: by outcome id; the two sides of one outcome stay side 0 then side 1.
 * - shares_asc / shares_desc: by shares held; ties by outcome id, then side.
 *
 * The choice is remembered per device (localStorage `outfox.positions.sort`). Every storage access is wrapped:
 * storage can be missing or throw (private mode, blocked site data); the in-memory copy then lasts for the session.
 */
import type { OutcomeHolding } from './derive';

export const POSITION_SORTS = ['value_desc', 'outcome_asc', 'outcome_desc', 'shares_asc', 'shares_desc'] as const;
export type PositionSort = (typeof POSITION_SORTS)[number];
export const DEFAULT_POSITION_SORT: PositionSort = 'value_desc';
export const POSITION_SORT_STORAGE_KEY = 'outfox.positions.sort';

/** Catalog key of each option's text. */
export const POSITION_SORT_LABEL = {
  value_desc: 'positions.sort.value_desc',
  outcome_asc: 'positions.sort.outcome_asc',
  outcome_desc: 'positions.sort.outcome_desc',
  shares_asc: 'positions.sort.shares_asc',
  shares_desc: 'positions.sort.shares_desc',
} as const satisfies Record<PositionSort, string>;

export function parsePositionSort(raw: string | null | undefined): PositionSort {
  return (POSITION_SORTS as readonly string[]).includes(raw ?? '') ? (raw as PositionSort) : DEFAULT_POSITION_SORT;
}

type Row = Pick<OutcomeHolding, 'outcome' | 'side' | 'shares'>;

const byId = (a: Row, b: Row) => a.outcome - b.outcome || a.side - b.side;

/** A sorted copy of `rows` (the input order is kept for value_desc and for full ties). */
export function sortPositions<T extends Row>(rows: readonly T[], sort: PositionSort): T[] {
  const out = [...rows];
  switch (sort) {
    case 'value_desc':
      return out;
    case 'outcome_asc':
      return out.sort(byId);
    case 'outcome_desc':
      return out.sort((a, b) => b.outcome - a.outcome || a.side - b.side);
    case 'shares_asc':
      return out.sort((a, b) => a.shares - b.shares || byId(a, b));
    case 'shares_desc':
      return out.sort((a, b) => b.shares - a.shares || byId(a, b));
  }
}

type Store = Pick<Storage, 'getItem' | 'setItem'>;

let memory: PositionSort | null = null;

function defaultStore(): Store | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function loadPositionSort(store: Store | undefined = defaultStore()): PositionSort {
  let raw: string | null = null;
  try {
    raw = store?.getItem(POSITION_SORT_STORAGE_KEY) ?? null;
  } catch {
    raw = null;
  }
  return raw !== null ? parsePositionSort(raw) : (memory ?? DEFAULT_POSITION_SORT);
}

export function savePositionSort(sort: PositionSort, store: Store | undefined = defaultStore()): void {
  memory = sort;
  try {
    store?.setItem(POSITION_SORT_STORAGE_KEY, sort);
  } catch {
    /* storage unavailable: the in-memory copy still applies for this session */
  }
}

/** Test hook: forget the in-memory fallback. */
export function resetPositionSortMemory(): void {
  memory = null;
}
